import { useState, useCallback, useRef } from 'react';
import IngredientAPI from '../api/ingredient.js';

/**
 * Hook for managing ingredient autocomplete state with debouncing.
 * Handles API search, client-side filtering, loading states, and error handling
 * with retry-on-retype behavior.
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {number} options.minQueryLength - Minimum characters to trigger search (default: 3)
 * @param {number} options.maxResults - Maximum suggestions to return (default: 20)
 * @returns {Object} Autocomplete state and functions
 */
export function useIngredientAutocomplete({
  debounceMs = 300,
  minQueryLength = 3,
  maxResults = 20,
} = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs for debouncing and tracking current query
  const debounceTimerRef = useRef(null);
  const currentQueryRef = useRef('');

  /**
   * Clear all state and cancel any pending requests
   */
  const clear = useCallback(() => {
    // Cancel any pending debounced search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    currentQueryRef.current = '';
    setSuggestions([]);
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * Execute the API search and filter results
   * @param {string} query - Search query
   */
  const executeSearch = useCallback(async (query) => {
    // Store the query we're searching for to handle race conditions
    const searchQuery = query;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await IngredientAPI.search(query);
      
      // Check if this is still the current query (handles race conditions)
      if (currentQueryRef.current !== searchQuery) {
        return;
      }
      
      // Extract ingredients from the response
      // The API returns { results: [...], pagination: {...} }
      const ingredients = response?.results || response?.ingredients || (Array.isArray(response) ? response : []);
      
      // Cap results at maxResults (API already handles search matching)
      const filtered = ingredients.slice(0, maxResults);
      
      setSuggestions(filtered);
      setIsLoading(false);
    } catch (err) {
      // Check if this is still the current query
      if (currentQueryRef.current !== searchQuery) {
        return;
      }
      
      // Handle API errors - allow retry by typing again
      setError('Unable to load suggestions. Please try again.');
      setSuggestions([]);
      setIsLoading(false);
    }
  }, [maxResults]);

  /**
   * Search for ingredients with debouncing.
   * Clears error state on new input (retry-on-retype behavior).
   * @param {string} query - Search query
   */
  const search = useCallback((query) => {
    // Update current query ref
    currentQueryRef.current = query;
    
    // Cancel any pending debounced search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Clear error on new input (retry-on-retype behavior)
    if (error) {
      setError(null);
    }
    
    // If query is too short, clear suggestions
    if (!query || query.length < minQueryLength) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }
    
    // Show loading state immediately for responsive UX
    setIsLoading(true);
    
    // Debounce the actual API call
    debounceTimerRef.current = setTimeout(() => {
      executeSearch(query);
    }, debounceMs);
  }, [debounceMs, minQueryLength, executeSearch, error]);

  return {
    suggestions,
    isLoading,
    error,
    search,
    clear,
  };
}

export default useIngredientAutocomplete;
